import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Typography,
    styled,
    Box,
    Autocomplete,
    TextField,
    FormControl,
    CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Spinner } from '../../atoms/Spinner/Spinner';
import { useTranslation } from 'react-i18next';
import { Toast } from '../../molecules/Toast/Toast';
import { useAppDispatch } from '../../../common/hooks';
import { transferPlans, fetchAdminsAndAdvisors } from '../../../features/adviceAdmin';

export type User = {
    id: string;
    name: string;
    email: string;
    role: 'admin' | 'advisor' | 'ftadmin';
};

type TransferPlansModalTwoWayProps = {
    open: boolean;
    onClose: () => void;
    fromUser: User;
    users: User[];
    loading?: boolean;
    refreshApprovals?: () => void;
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiPaper-root': {
        width: 550,
        maxWidth: 550,
        borderRadius: theme.spacing(2),
        margin: 'auto'
    }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
    fontWeight: 600,
    color: 'black',
    paddingBottom: theme.spacing(0.5),
    paddingRight: 0,
    position: 'relative'
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    padding: theme.spacing(0.25),
    color: theme.palette.grey[700],
    '&:hover': {
        backgroundColor: 'transparent',
        color: 'black'
    }
}));

const FieldContainer = styled('div')(({ theme }) => ({
    marginBottom: theme.spacing(3)
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.primary
}));

const MenuItemContent = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
    margin: 0
});

const EmailText = styled(Typography)(({ theme }) => ({
    color: theme.palette.grey[500]
}));

const TransferButton = styled(Button)({
    textTransform: 'none',
    borderRadius: 10,
    padding: '6px 16px',
    minWidth: 'auto'
});

const ErrorMessage = styled(Typography)(({ theme }) => ({
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
    fontWeight: 400
}));

const TwoWayTransferPlansModal: React.FC<TransferPlansModalTwoWayProps> = ({
    open,
    onClose,
    fromUser,
    users,
    refreshApprovals
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [fromUserId, setFromUserId] = useState(fromUser.id);
    const [isToFocused, setIsToFocused] = useState(false);
    const [isFromFocused, setIsFromFocused] = useState(false);
    const [toUserId, setToUserId] = useState('');
    const [loading, setLoading] = useState(false);
    const [toastOpen, setToastOpen] = useState(false);
    const [toastMsg, setToastMsg] = useState('');
    const [toastHeader, setToastHeader] = useState('');
    const [toastType, setToastType] = useState<'confirm' | 'error'>('confirm');
    const [toastKey, setToastKey] = useState(0);
    const fromRef = React.useRef<HTMLDivElement>(null);
    const toRef = React.useRef<HTMLDivElement>(null);
    const [searchResults, setSearchResults] = useState<User[]>([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [fromInputValue, setFromInputValue] = useState('');
    const [toInputValue, setToInputValue] = useState('');
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (open) {
            setFromUserId(fromUser.id);
            setToUserId('');
            setSearchResults([]);
            setFromInputValue('');
            setToInputValue('');
        }
    }, [open, fromUser.id]);

    const searchUsers = useCallback(
        async (searchTerm: string) => {
            if (!searchTerm) {
                setSearchResults([]);
                return;
            }

            setSearchLoading(true);
            try {
                const result: any = await dispatch(
                    fetchAdminsAndAdvisors({
                        page: 1,
                        page_size: 50,
                        search_term: searchTerm.trim(),
                        selected_role: 'allroles',
                        updateState: false
                    })
                );

                if (result.meta.requestStatus === 'fulfilled' && result.payload?.users) {
                    const formattedUsers = result.payload.users.map((user: any) => ({
                        id: user.id,
                        name: user.userName || user.adminName || '',
                        email: user.userEmail || user.adminEmail || '',
                        role: user.userType || 'advisor'
                    }));
                    setSearchResults(formattedUsers);
                } else {
                    setSearchResults([]);
                }
            } catch (error) {
                console.error('Error searching users:', error);
                setSearchResults([]);
            } finally {
                setSearchLoading(false);
            }
        },
        [dispatch]
    );
    const getFromUserObject = (id: any): User | null => {
        if (!id) {
            return null;
        }
        return searchResults.find((u) => u.id === id) || users.find((u) => u.id === id) || fromUser;
    };
    const getToUserObject = (id: string) =>
        searchResults.find((u) => u.id === id) || users.find((u) => u.id === id) || null;

    const handleToFocus = () => {
        setIsToFocused(true);
        if (toUserId && toRef.current) {
            console.log('toref', toRef.current);
            const popupIndicator = toRef.current.querySelector('.MuiAutocomplete-popupIndicator');
            if (popupIndicator instanceof HTMLElement) {
                popupIndicator.click();
            }
        }
    };

    const handleToBlur = () => {
        setIsToFocused(false);
    };

    const handleFromFocus = () => {
        setIsFromFocused(true);
        if (fromUserId && fromRef.current) {
            const popupIndicator = fromRef.current.querySelector('.MuiAutocomplete-popupIndicator');
            if (popupIndicator instanceof HTMLElement) {
                popupIndicator.click();
            }
        }
    };

    const handleFromBlur = () => {
        setIsFromFocused(false);
    };

    // Validation
    const isSameUserSelected = fromUserId === toUserId && fromUserId !== '';
    const isTransferButtonDisabled = !fromUserId || !toUserId || isSameUserSelected || loading;

    const renderUserContent = (u: any) => (
        <MenuItemContent>
            <Typography fontWeight={500}>{u.name}</Typography>
            <EmailText variant="body2">{u.email}</EmailText>
            <Typography variant="caption" color="primary" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                {u.role}
            </Typography>
        </MenuItemContent>
    );

    const renderAutocompleteInput = (
        params: any,
        currentUserId: string,
        placeholderText: string,
        isFocused: boolean,
        componentRef: React.RefObject<HTMLDivElement>
    ) => {
        const selectedUser = currentUserId ? getFromUserObject(currentUserId) || getToUserObject(currentUserId) : null;
        const showCustomUiForSelection = selectedUser && !isFocused;
        const inputValue = isFocused ? params.inputProps.value : selectedUser ? '' : params.inputProps.value;
        const finalPlaceholder = showCustomUiForSelection ? '' : placeholderText;
        console.log('componentRef', componentRef.current, params);
        const handleAdornmentClick = (event: React.MouseEvent) => {
            event.stopPropagation();
            params.inputProps.onFocus(event);
            if (componentRef.current) {
                const popupIndicator = componentRef.current.querySelector('.MuiAutocomplete-popupIndicator');
                if (popupIndicator instanceof HTMLElement) {
                    setTimeout(() => {
                        popupIndicator.click();
                    }, 10);
                }
            }
        };

        return (
            <TextField
                {...params}
                inputProps={{
                    ...params.inputProps,
                    value: inputValue
                }}
                placeholder={finalPlaceholder}
                InputProps={{
                    ...params.InputProps,
                    startAdornment: showCustomUiForSelection && selectedUser && (
                        <Box sx={{ ml: 1, my: 0 }} onClick={handleAdornmentClick}>
                            {renderUserContent(selectedUser)}
                        </Box>
                    ),
                    sx: {
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: (theme) => theme.palette.grey[300],
                            borderRadius: '8px'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: (theme) => theme.palette.grey[500] + '!important'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: (theme) => theme.palette.primary[500]
                        }
                    }
                }}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        minHeight: '95px',
                        py: '4px',
                        paddingRight: '32px !important'
                    },
                    '& .MuiInputBase-input': {
                        display: 'block',
                        caretColor: showCustomUiForSelection ? 'transparent' : 'auto',
                        padding: showCustomUiForSelection
                            ? '0px 14px 0px 14px !important'
                            : '14px 14px 14px 14px !important',
                        '&::placeholder': {
                            color: (theme) => theme.palette.grey[500],
                            opacity: 1
                        }
                    }
                }}
            />
        );
    };

    const handleTransfer = async () => {
        if (isTransferButtonDisabled) return;
        setLoading(true);
        try {
            const res: any = await dispatch(transferPlans({ fromUser: fromUserId, toUser: toUserId }));

            if (res.meta.requestStatus === 'fulfilled' && res.payload?.success) {
                if (refreshApprovals) {
                    refreshApprovals();
                }
                const selectedUser = users.find((u) => u.id === toUserId);
                const msg = res.payload.message?.includes('No client profiles found')
                    ? res.payload.message
                    : `${t('TRANSFER_TO_TEXT')} ${selectedUser?.email || ''}`;

                setToastMsg(msg);
                setToastHeader(
                    msg.includes('No client profiles found') ? t('NO_TRANSFER') : t('TOASTER_TRANSFER_SUCCESSFUL')
                );
                setToastType('confirm');
            } else {
                setToastMsg('');
                setToastHeader(t('TRANSFER_FAILED_TEXT'));
                setToastType('error');
            }

            setToastKey((prev) => prev + 1);
            setToastOpen(true);
            setToUserId('');
            onClose();
        } catch (err) {
            console.error(err);
            setToastMsg('');
            setToastHeader(t('TRANSFER_FAILED_TEXT'));
            setToastType('error');
            setToastKey((prev) => prev + 1);
            setToastOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setIsToFocused(false);
        setIsFromFocused(false);
        setFromUserId(fromUser.id);
        setToUserId('');
        setSearchResults([]);
        setFromInputValue('');
        setToInputValue('');
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        onClose();
    };

    const handleInputChange = (value: string, field: 'from' | 'to', reason: string) => {
        if (field === 'from') {
            setFromInputValue(value);
        } else {
            setToInputValue(value);
        }

        // Only search when user is typing, not when selecting or resetting
        if (reason !== 'input') {
            return;
        }

        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            searchUsers(value);
        }, 1000);
    };

    const uniqueUsers = useMemo(() => {
        const seenIds = new Set();
        return users.filter((user) => {
            const isDuplicate = seenIds.has(user.id);
            seenIds.add(user.id);
            return !isDuplicate;
        });
    }, [users]);

    return (
        <>
            {loading ? (
                <Spinner enabled={loading} />
            ) : (
                <StyledDialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                    <StyledDialogTitle>
                        {t('TRANSFER_PLANS_TEXT')}
                        <CloseButton onClick={handleClose}>
                            <CloseIcon fontSize="small" />
                        </CloseButton>
                    </StyledDialogTitle>

                    <DialogContent>
                        <Box>
                            <FieldContainer>
                                <FieldLabel variant="body2">{t('From')}</FieldLabel>
                                <FormControl fullWidth>
                                    <Autocomplete
                                        options={fromInputValue.trim().length !== 0 ? searchResults : uniqueUsers}
                                        isOptionEqualToValue={(option, value) =>
                                            value === undefined || value === null || option.id === value.id
                                        }
                                        noOptionsText={searchLoading ? t('Loading...') : t('NO_ADVISOR_OR_ADMIN_FOUND')}
                                        filterOptions={(options) => options}
                                        ref={fromRef}
                                        sx={{
                                            '& .MuiAutocomplete-popupIndicator': {
                                                transform: 'none',
                                                transition: 'none'
                                            }
                                        }}
                                        onClose={handleFromBlur}
                                        onFocus={handleFromFocus}
                                        onBlur={handleFromBlur}
                                        value={getFromUserObject(fromUserId)}
                                        onChange={(event, newValue: User | null) => {
                                            setFromUserId(newValue ? newValue.id : '');
                                        }}
                                        onInputChange={(event, value, reason) => {
                                            if (event) {
                                                handleInputChange(value, 'from', reason);
                                            }
                                        }}
                                        loading={searchLoading}
                                        getOptionLabel={() => (isFromFocused ? '' : '')}
                                        popupIcon={searchLoading ? <CircularProgress size={20} /> : <SearchIcon />}
                                        renderOption={(props, option: User) => (
                                            <li {...props} key={option.id}>
                                                {renderUserContent(option)}
                                            </li>
                                        )}
                                        renderInput={(params) =>
                                            renderAutocompleteInput(
                                                params,
                                                fromUserId,
                                                t('CHOOSE_AN_ADVISOR_OR_ADMIN'),
                                                isFromFocused,
                                                fromRef
                                            )
                                        }
                                        ListboxProps={{
                                            sx: {
                                                maxHeight: 48 * 6 + 8,
                                                overflowY: 'auto',
                                                mt: 0,
                                                '& li': {
                                                    minHeight: '64px',
                                                    paddingTop: '8px',
                                                    paddingBottom: '8px',
                                                    alignItems: 'flex-start'
                                                }
                                            }
                                        }}
                                    />
                                </FormControl>
                            </FieldContainer>
                            <FieldContainer>
                                <FieldLabel variant="body2">{t('To')}</FieldLabel>
                                <FormControl fullWidth>
                                    <Autocomplete
                                        options={toInputValue.trim().length !== 0 ? searchResults : uniqueUsers}
                                        popupIcon={searchLoading ? <CircularProgress size={20} /> : <SearchIcon />}
                                        getOptionLabel={(option) => (isToFocused ? option.email : '')}
                                        onClose={handleToBlur}
                                        noOptionsText={searchLoading ? t('Loading...') : t('NO_ADVISOR_OR_ADMIN_FOUND')}
                                        ref={toRef}
                                        onFocus={handleToFocus}
                                        onBlur={handleToBlur}
                                        sx={{
                                            '& .MuiAutocomplete-popupIndicator': {
                                                transform: 'none',
                                                transition: 'none'
                                            }
                                        }}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        filterOptions={(options) => options}
                                        loading={searchLoading}
                                        value={getToUserObject(toUserId)}
                                        onChange={(event, newValue: User | null) => {
                                            setToUserId(newValue ? newValue.id : '');
                                        }}
                                        onInputChange={(event, value, reason) => {
                                            if (event) {
                                                handleInputChange(value, 'to', reason);
                                            }
                                        }}
                                        renderOption={(props, option: User) => (
                                            <li {...props} key={option.id}>
                                                {renderUserContent(option)}
                                            </li>
                                        )}
                                        renderInput={(params) =>
                                            renderAutocompleteInput(
                                                params,
                                                toUserId,
                                                t('CHOOSE_AN_ADVISOR_OR_ADMIN'),
                                                isToFocused,
                                                toRef
                                            )
                                        }
                                        ListboxProps={{
                                            sx: {
                                                maxHeight: 48 * 6 + 8,
                                                overflowY: 'auto',
                                                mt: 0,
                                                '& li': {
                                                    minHeight: '64px',
                                                    paddingTop: '8px',
                                                    paddingBottom: '8px',
                                                    alignItems: 'flex-start'
                                                }
                                            }
                                        }}
                                    />
                                </FormControl>
                                {isSameUserSelected && (
                                    <ErrorMessage>{t('PLANS_CANNOT_BE_TRANSFERRED_TO_SAME_ACCOUNT')}</ErrorMessage>
                                )}
                            </FieldContainer>
                        </Box>
                    </DialogContent>

                    <DialogActions sx={{ p: 2, pt: 1.5, px: 3 }}>
                        <TransferButton
                            variant="contained"
                            disabled={isTransferButtonDisabled}
                            onClick={handleTransfer}
                        >
                            {t('FINANCIAL_ADMIN_TABLE_PLAN_TRANSFER_ACTION')}
                        </TransferButton>
                    </DialogActions>
                </StyledDialog>
            )}
            {toastOpen && (
                <Toast
                    key={toastKey}
                    heading={toastHeader}
                    type={toastType}
                    messages={toastMsg ? [toastMsg] : []}
                    isTimer
                    disappearTime={10}
                    handleClose={() => setToastOpen(false)}
                />
            )}
        </>
    );
};

export default TwoWayTransferPlansModal;
